<script>
import ClientInfo from '@components/client/client-info.vue';
import moment from 'moment';
import NotificationBubble from '@components/notifications/notification-bubble.vue';
import resize from '@utils/image-sizer';
import {
  authComputed,
  clientComputed,
  clientMethods,
  mediaComputed,
  prospectComputed,
  sidebarComputed,
  trainerComputed,
  organizationComputed,
} from '@state/helpers';

export default {
  components: {
    ClientInfo,
    NotificationBubble,
  },
  props: {
    client: {
      type: Object,
      required: false,
      default: () => {},
    },
    prospect: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      imgPlaceHolder: require('@assets/images/placeholder-photo-upload.svg'),
      avatarPlaceHolder: require('@assets/images/placeholder-avatar.png'),
    };
  },
  computed: {
    ...authComputed,
    ...clientComputed,
    ...mediaComputed,
    ...prospectComputed,
    ...sidebarComputed,
    ...trainerComputed,
    ...organizationComputed,
    isImperial() {
      // Dynamically check trainer's preferred unit
      // Here not every trainer have the 'preferredUnit' flag so the default in imperial unless set metric
      return (
        this.currentUser &&
        this.currentUser.roles &&
        this.currentUser.roles.isTrainer &&
        this.currentUser.trainer &&
        this.currentUser.trainer.preferredUnit &&
        this.currentUser.trainer.preferredUnit !== 'metric'
      );
    },
    clientAvatar() {
      return this.selectedClient &&
        this.selectedClient.profile.mediaAssets &&
        this.selectedClient.profile.mediaAssets.avatarImage &&
        this.selectedClient.profile.mediaAssets.avatarImage.url
        ? resize(
            this.selectedClient.profile.mediaAssets.avatarImage.url,
            90,
            90
          )
        : this.avatarPlaceHolder;
    },
    prospectAvatar() {
      return this.selectedProspect &&
        this.selectedProspect.mediaAssets &&
        this.selectedProspect.mediaAssets.avatarImage &&
        this.selectedProspect.mediaAssets.avatarImage.url
        ? resize(this.selectedProspect.mediaAssets.avatarImage.url, 90, 90)
        : this.avatarPlaceHolder;
    },
    gender() {
      return this.selectedClient && this.selectedClient.profile.gender
        ? this.selectedClient.profile.gender.toLowerCase() === 'male'
          ? 'M'
          : this.selectedClient.profile.gender.toLowerCase() === 'female'
          ? 'F'
          : null
        : null;
    },
    clientHeight() {
      // Dynamically calculate height based on trainer's preferrence
      const feet =
        this.selectedClient &&
        this.selectedClient.progress.initial.height &&
        this.selectedClient.progress.initial.height.value &&
        this.selectedClient.progress.initial.height.isImperial
          ? this.selectedClient.progress.initial.height.value >= 12
            ? Math.floor(this.selectedClient.progress.initial.height.value / 12)
            : 0
          : null;

      const inches =
        this.selectedClient &&
        this.selectedClient.progress.initial.height &&
        this.selectedClient.progress.initial.height.value &&
        this.selectedClient.progress.initial.height.isImperial
          ? this.selectedClient.progress.initial.height.value >= 12
            ? this.selectedClient.progress.initial.height.value - feet * 12
            : this.selectedClient.progress.initial.height.value
          : null;
      if (feet && inches) {
        return this.isImperial
          ? `${feet}' ${inches}"`
          : `${Math.round((feet * 12 + inches) * 2.54)}cm`; // If feet and inches
      } else if (feet && !inches) {
        // If inches are zero (even number/feet only)
        return this.isImperial
          ? `${feet}'`
          : `${Math.round(feet * 12 * 2.54)}cm`;
      } else if (!feet && inches) {
        // Otherwise only inches
        return this.isImperial
          ? `${inches}"`
          : `${Math.round(inches * 2.54)}cm`; // Otherwise it's only inches
      } else {
        return ``;
      }
    },
    dob() {
      return this.selectedClient && this.selectedClient.profile.dob
        ? moment.utc(this.selectedClient.profile.dob).format('MM/DD/YY') // Set the correct format
        : null;
    },
    hasUpdates() {
      return (
        !this.visitedClient &&
        this.clientActivity &&
        this.clientActivity.lastUpdate &&
        moment(this.clientActivity.lastUpdate).isSame(moment(), 'week')
      );
    },
  },
  mounted() {
    if (this.$route.name !== 'clientActivity') {
      this.setVisitedClient(true);
    }
  },
  destroyed() {
    if (
      this.$route.name !== 'clientActivity' &&
      this.$route.name !== 'clientMessages' &&
      this.$route.name !== 'clientPhotos' &&
      this.$route.name !== 'clientAbout' &&
      this.$route.name !== 'clientProgram' &&
      this.$route.name !== 'clientSubscription' &&
      this.$route.name !== 'clientTrainerNotes' &&
      this.$route.name !== 'clientCheckIn'
    ) {
      this.setVisitedClient(false);
    }
  },
  methods: {
    ...clientMethods,
  },
};
</script>

<template>
  <div>
    <div v-if="selectedProspect">
      <div
        :class="[
          'sidebar-section',
          { 'sidebar-collapsed': isSidebarCollapsed },
        ]"
      >
        <ClientInfo
          :isSidebarCollapsed="isSidebarCollapsed"
          :fullName="
            `${selectedProspect.firstName} ${selectedProspect.lastName}`
          "
          :avatar="prospectAvatar"
        />
        <ul class="prospect-onboarding-progress">
          <li class="sidebar-nav-list-item">
            <router-link
              :to="{
                name: 'prospectProfile',
                params: {
                  id: selectedProspect._id,
                  prospect: selectedProspect,
                },
              }"
              replace
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'sidebar-list-icon icon-user'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5v14a2 2 0 002 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5a2 2 0 00-2 2zm12 4c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3zm-3 4.9c-2 0-6 1.1-6 3.1v1h12v-1c0-2-4-3.1-6-3.1z" fill="#fff"/></svg>
              <span v-if="!isSidebarCollapsed">Profile</span>
            </router-link>
          </li>
          <li class="sidebar-nav-list-item">
            <router-link
              :to="{
                name: 'prospectMessages',
                params: {
                  id: selectedProspect._id,
                  prospect: selectedProspect,
                },
              }"
              replace
            >
              <span class="link-text-wrapper">
                <NotificationBubble
                  v-if="selectedProspect.unreadCount"
                  :minimal="true"
                />
                <img
                  class="sidebar-list-icon icon-photos"
                  src="@assets/images/icon-chat.svg"
                />
                <span v-if="!isSidebarCollapsed">Messages</span>
              </span>
            </router-link>
          </li>
        </ul>
      </div>
    </div>
    <div v-if="selectedClient">
      <div
        :class="[
          'sidebar-section',
          { 'sidebar-collapsed': isSidebarCollapsed },
        ]"
      >
        <ClientInfo
          :isSidebarCollapsed="isSidebarCollapsed"
          :fullName="
            `${selectedClient.profile.firstName} ${selectedClient.profile.lastName}`
          "
          :avatar="clientAvatar"
          :gender="gender"
          :dob="dob"
          :userHeight="clientHeight"
        />
      </div>
      <div
        :class="[
          'sidebar-section',
          { 'sidebar-collapsed': isSidebarCollapsed },
        ]"
      >
        <ul>
          <li class="sidebar-nav-list-item">
            <router-link
              :to="{
                name: 'clientProgram',
                params: { id: selectedClient._id, client: selectedClient },
              }"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'sidebar-list-icon icon-cal-alt'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.4 0v1.2H3.6V0H2.4v1.2h-.6c-.666 0-1.194.54-1.194 1.2L.6 10.8c0 .66.534 1.2 1.2 1.2h8.4c.66 0 1.2-.54 1.2-1.2V2.4c0-.66-.54-1.2-1.2-1.2h-.6V0H8.4zM9 6.6H6v3h3v-3zm-7.2 4.2h8.4V4.2H1.8v6.6z" fill="#fff"/></svg>
              <span v-if="!isSidebarCollapsed">Program</span>
            </router-link>
          </li>
          <li class="sidebar-nav-list-item">
            <router-link
              :to="{
                name: 'clientMessages',
                params: { id: selectedClient._id, client: selectedClient },
              }"
            >
              <span class="link-text-wrapper">
                <NotificationBubble
                  v-if="selectedClient.hasUnreadMessages"
                  :minimal="true"
                />
                <img
                  class="sidebar-list-icon icon-photos"
                  src="@assets/images/icon-chat.svg"
                />
                <span v-if="!isSidebarCollapsed">Messages</span>
              </span>
            </router-link>
          </li>
          <li class="sidebar-nav-list-item user">
            <router-link
              :to="{
                name: 'clientAbout',
                params: { id: selectedClient._id, client: selectedClient },
              }"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'sidebar-list-icon icon-user'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M3 5v14a2 2 0 002 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5a2 2 0 00-2 2zm12 4c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3 3 1.34 3 3zm-3 4.9c-2 0-6 1.1-6 3.1v1h12v-1c0-2-4-3.1-6-3.1z" fill="#fff"/></svg>
              <span v-if="!isSidebarCollapsed">About</span>
            </router-link>
          </li>
          <li class="sidebar-nav-list-item user">
            <router-link
              :to="{
                name: 'clientCheckIn',
                params: { id: selectedClient._id, client: selectedClient },
              }"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'sidebar-list-icon icon-check-in'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M2 0h14c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V2C0 .9.9 0 2 0zm0 16h14V2H2v14z" fill="#000"/><path fill-rule="evenodd" clip-rule="evenodd" d="M5.075 9L3.5 5.614S6.012 4 9 4s5.5 1.614 5.5 1.614L12.89 9S11 7.95 9 7.976C7 8.002 5.075 9 5.075 9z" fill="#000"/></svg>
              <span v-if="!isSidebarCollapsed">Check-In</span>
            </router-link>
          </li>
          <li class="sidebar-nav-list-item">
            <router-link
              :to="{
                name: 'clientPhotos',
                params: { id: selectedClient._id, client: selectedClient },
              }"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'sidebar-list-icon icon-photos'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M18 12.6V1.8c0-.99-.81-1.8-1.8-1.8H5.4c-.99 0-1.8.81-1.8 1.8v10.8c0 .99.81 1.8 1.8 1.8h10.8c.99 0 1.8-.81 1.8-1.8zM8.1 9l1.827 2.439L12.6 8.1l3.6 4.5H5.4L8.1 9zM0 16.2V3.6h1.8v12.6h12.6V18H1.8C.81 18 0 17.19 0 16.2z" fill="#000"/></svg>
              <span v-if="!isSidebarCollapsed">Photos</span>
            </router-link>
          </li>
          <li class="sidebar-nav-list-item user">
            <router-link
              :to="{
                name: 'clientSubscription',
                params: { id: selectedClient._id, client: selectedClient },
              }"
            >
              <img
                class="sidebar-list-icon icon-subscription"
                svg-inline
                src="@assets/images/icon-subscription.png"
              />
              <span v-if="!isSidebarCollapsed"
                >{{ organizationTitle }} PAY</span
              >
            </router-link>
          </li>
          <li class="sidebar-nav-list-item user">
            <router-link
              :to="{
                name: 'clientTrainerNotes',
                params: { id: selectedClient._id, client: selectedClient },
              }"
            >
              <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'sidebar-list-icon icon-check-in'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#000" d="M0 0h16v2H0zM0 5h16v2H0zM0 10h10v2H0z"/></svg>
              <span v-if="!isSidebarCollapsed">Trainer's Notes</span>
            </router-link>
          </li>
          <li class="sidebar-nav-list-item user">
            <router-link
              :to="{
                name: 'clientNutrition',
                params: { id: selectedClient._id, client: selectedClient },
              }"
            >
              <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'sidebar-list-icon icon-subscription'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M14.4 14.4c3.4-3.4 3.4-8.9 0-12.2L2.2 14.4c3.3 3.4 8.8 3.4 12.2 0zM14 5c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm0 4c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm-4 0c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm0 4c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm-4 2c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm10.6 1.6c-4.6 4.6-12 4.6-16.6 0l1.4-1.4c3.8 3.8 9.9 3.8 13.7 0 3.8-3.8 3.8-9.9 0-13.7L16.6 0c4.5 4.6 4.5 12 0 16.6z" fill="#000"/></svg>
              <span v-if="!isSidebarCollapsed">Nutrition</span>
            </router-link>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.sidebar-section {
  .sidebar-nav-list-item {
    margin-bottom: 1rem;
    padding: 0 2rem;
    position: relative;

    &:hover {
      cursor: pointer;
    }

    &.user {
      padding: 0 1.7rem;
    }

    .link-text-wrapper {
      position: relative;
    }

    .sidebar-list-icon {
      margin-right: 1rem;

      &.icon-bar-graph,
      &.icon-photos {
        fill: none;
        height: 22px;
        width: 22px;
      }

      &.icon-check-in {
        height: 22px;
        margin-left: 0.3rem;
        width: 22px;
      }

      &.icon-user,
      &.icon-cal-alt {
        path {
          fill: black;
        }
      }

      &.icon-subscription {
        border-radius: 3px;
        height: 21px;
        margin-left: 4px;
        width: 21px;
      }

      &.icon-user {
        height: 28px;
        margin-right: 7px;
        width: 28px;
      }
      &.icon-cal-alt {
        height: 22px;
        width: 22px;
      }
    }

    a {
      color: $color-medium-gray-alt;
      font-size: 1.4rem;
      font-weight: 600;
      letter-spacing: 0.116667px;
      line-height: 2.2rem;
      text-decoration: none;
      white-space: nowrap;

      &.router-link-exact-active {
        color: $color-heading-text;

        &::after {
          background-color: $color-action;
          content: '';
          display: block;
          height: 100%;
          position: absolute;
          right: 0;
          top: 0;
          width: 0.9rem;
        }
      }
    }
  }

  &.sidebar-collapsed {
    ::v-deep .thumbnail-wrapper {
      margin-bottom: 2rem;
      padding: 0 1rem;

      .image-thumbnail {
        height: 3.3rem;
        width: 3.3rem;
      }
    }

    .sidebar-nav-list-item {
      text-align: center;
    }

    a.router-link-exact-active::after {
      width: 0.5rem;
    }
  }
}
</style>
